/**
 * AgGridWrapper Component
 *
 * A unified wrapper component for AG Grid that provides:
 * - Consistent Paper container with theme class
 * - Pre-configured AgGridReact with mode-based defaults
 * - Exposed ref for grid API access
 * - Support for all three modes via `mode` prop
 *
 * Usage:
 *   <AgGridWrapper
 *     mode={TABLE_MODES.COMPSHEET}
 *     rowData={data}
 *     columnDefs={columns}
 *     numericalFields={NUMERICAL_FIELDS}
 *     pinnedBottomRowData={pinnedData}
 *     width={tableWidth}
 *     maxHeight={tableHeight}
 *   />
 */

import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { Box, Paper } from '@mui/material';
import { AgGridReact } from '@ag-grid-community/react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';

import useAgGrid from '../../hooks/useAgGrid';
import { TABLE_MODES } from '../../constants/agGridStyles';
import { AG_GRID_SIZING, calculateAgGridTableSize } from '../../utils/agGridSizing';

/**
 * AgGridWrapper component props
 *
 * @param {string} mode - Table mode (TABLE_MODES.COMPSHEET | TABLE_MODES.FINANCIAL | TABLE_MODES.INDEX)
 * @param {Array} rowData - Data to display in the grid
 * @param {Array} columnDefs - Column definitions
 * @param {Array} numericalFields - Fields for Mean/Median calculations (CompSheet mode)
 * @param {boolean} showPinnedStats - Whether to show Mean/Median pinned rows (default: true for CompSheet)
 * @param {Array} pinnedBottomRowData - Custom pinned bottom row data (overrides calculated stats)
 * @param {string|number} width - Table width (CSS value or number for pixels)
 * @param {string|number} maxWidth - Maximum table width
 * @param {string|number} height - Table height
 * @param {string|number} maxHeight - Maximum table height
 * @param {string} sizing - Sizing strategy (legacy | content)
 * @param {string} fitMode - Content sizing fit behavior (columns | container)
 * @param {number} minTableWidth - Minimum generated width for content sizing
 * @param {number} maxTableWidth - Maximum generated width for content sizing
 * @param {number} minTableHeight - Minimum generated height for content sizing
 * @param {number} maxTableHeight - Maximum generated height for content sizing
 * @param {Function} onGridReady - Custom grid ready handler
 * @param {Function} onFilterChanged - Custom filter changed handler
 * @param {Function} onSortChanged - Custom sort changed handler
 * @param {Function} onRowClicked - Row click handler
 * @param {Object} cssVarOverrides - Custom CSS variable overrides
 * @param {Object} sxOverrides - Custom sx prop overrides
 * @param {Object} paperSx - Additional sx props for Paper component
 * @param {string} surface - Outer surface behavior (paper | bare)
 * @param {Object} gridProps - Additional props to pass to AgGridReact
 */
const AgGridWrapper = forwardRef(
  (
    {
      mode = TABLE_MODES.COMPSHEET,
      rowData,
      columnDefs,
      numericalFields = [],
      showPinnedStats = true,
      pinnedBottomRowData: customPinnedData,
      width,
      maxWidth = '98%',
      height = '100%',
      maxHeight,
      sizing = AG_GRID_SIZING.LEGACY,
      minTableWidth,
      maxTableWidth,
      minTableHeight,
      maxTableHeight,
      tableWidthBuffer,
      columnBorderReserve,
      tableHeightBuffer,
      fitMode,
      pinnedRowCount,
      reserveScrollbar,
      onGridReady: customOnGridReady,
      onFilterChanged: customOnFilterChanged,
      onSortChanged: customOnSortChanged,
      onRowClicked,
      cssVarOverrides = {},
      sxOverrides = {},
      paperSx = {},
      surface = 'paper',
      ...gridProps
    },
    ref
  ) => {
    // Use the custom hook for all AG Grid configuration
    const {
      gridRef,
      themeClass,
      cssVars,
      sx,
      gridProps: hookGridProps,
      pinnedBottomRowData: calculatedPinnedData,
      onGridReady: hookOnGridReady,
      onFilterChanged: hookOnFilterChanged,
      onSortChanged: hookOnSortChanged,
      calculateTableWidth,
      calculateTableHeight,
      getGridApi,
      getColumnApi,
    } = useAgGrid({
      mode,
      numericalFields,
      showPinnedStats: mode === TABLE_MODES.COMPSHEET && showPinnedStats,
      cssVarOverrides,
      sxOverrides,
    });

    // Expose grid API methods via ref
    useImperativeHandle(ref, () => ({
      get api() {
        return getGridApi();
      },
      get columnApi() {
        return getColumnApi();
      },
      getGridApi,
      getColumnApi,
      calculateTableWidth,
      calculateTableHeight,
    }));

    // Merge custom handlers with hook handlers
    const handleGridReady = (params) => {
      hookOnGridReady(params);
      customOnGridReady?.(params);
    };

    const handleFilterChanged = (params) => {
      hookOnFilterChanged(params);
      customOnFilterChanged?.(params);
    };

    const handleSortChanged = (params) => {
      hookOnSortChanged(params);
      customOnSortChanged?.(params);
    };

    // Determine pinned data
    const pinnedData =
      customPinnedData !== undefined
        ? customPinnedData
        : mode === TABLE_MODES.COMPSHEET && showPinnedStats
        ? calculatedPinnedData
        : undefined;

    const useContentSizing = sizing === AG_GRID_SIZING.CONTENT;
    const contentSize = useMemo(
      () =>
        useContentSizing
          ? calculateAgGridTableSize({
              columnDefs,
              rowData,
              mode,
              includePinnedRows: mode === TABLE_MODES.COMPSHEET && showPinnedStats,
              pinnedRowCount: pinnedRowCount ?? (Array.isArray(pinnedData) ? pinnedData.length : undefined),
              headerHeight: gridProps.headerHeight,
              groupHeaderHeight: gridProps.groupHeaderHeight,
              rowHeight: gridProps.rowHeight,
              minWidth: minTableWidth,
              maxWidth: maxTableWidth,
              widthBuffer: tableWidthBuffer,
              columnBorderReserve,
              fitMode,
              minHeight: minTableHeight,
              maxHeight: maxTableHeight,
              heightBuffer: tableHeightBuffer,
              reserveScrollbar,
            })
          : null,
      [
        columnDefs,
        rowData,
        mode,
        showPinnedStats,
        pinnedData,
        pinnedRowCount,
        minTableWidth,
        maxTableWidth,
        minTableHeight,
        maxTableHeight,
        tableWidthBuffer,
        columnBorderReserve,
        tableHeightBuffer,
        fitMode,
        gridProps.headerHeight,
        gridProps.groupHeaderHeight,
        gridProps.rowHeight,
        reserveScrollbar,
        useContentSizing,
      ]
    );

    // Calculate dimensions if not provided
    const tableWidth =
      width || (useContentSizing ? contentSize?.width : columnDefs ? calculateTableWidth(columnDefs) : '100%');
    const resolvedHeight = useContentSizing && height === '100%' ? contentSize?.height : height;
    const tableHeight =
      maxHeight ||
      (useContentSizing
        ? contentSize?.maxHeight
        : rowData
          ? calculateTableHeight(rowData.length, mode === TABLE_MODES.COMPSHEET && showPinnedStats)
          : 500);

    const isBareSurface = surface === 'bare';
    const SurfaceComponent = isBareSurface ? Box : Paper;
    const surfaceProps = isBareSurface
      ? { 'data-surface': 'bare' }
      : { elevation: 2, 'data-surface': 'paper' };

    return (
      <SurfaceComponent
        data-testid="ag-grid-wrapper"
        {...surfaceProps}
        className={themeClass}
        style={{
          width: typeof tableWidth === 'number' ? `${tableWidth}px` : tableWidth,
          maxWidth,
          height: typeof resolvedHeight === 'number' ? `${resolvedHeight}px` : resolvedHeight,
          maxHeight: typeof tableHeight === 'number' ? `${tableHeight}px` : tableHeight,
          ...cssVars,
        }}
        sx={{
          alignSelf: 'center',
          mx: 'auto',
          ...sx,
          '& .ag-root-wrapper': {
            ...(sx['& .ag-root-wrapper'] || {}),
            border: 'none',
            borderRadius: 'inherit',
            overflow: 'hidden',
          },
          '& .ag-root': {
            ...(sx['& .ag-root'] || {}),
            borderRadius: 'inherit',
          },
          '& .ag-header': {
            ...(sx['& .ag-header'] || {}),
            borderTopLeftRadius: 'inherit',
            borderTopRightRadius: 'inherit',
            overflow: 'hidden',
          },
          ...(isBareSurface
            ? {
                boxShadow: 'none',
                backgroundColor: 'transparent',
              }
            : null),
          ...paperSx,
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          pinnedBottomRowData={pinnedData}
          onGridReady={handleGridReady}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          onRowClicked={onRowClicked}
          {...hookGridProps}
          {...gridProps}
        />
      </SurfaceComponent>
    );
  }
);

AgGridWrapper.displayName = 'AgGridWrapper';

export default AgGridWrapper;
